// src/components/publishing/publish-confirm-card.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Send, X, Loader2, AlertTriangle, CheckCircle2, ExternalLink,
  FileCode2, Globe, Linkedin,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  Draft, Integration, PublishGate, PublishResponse,
} from "@/app/services/publishing/publishing-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";

interface PublishConfirmCardProps {
  draft: Draft;
  integrations: Integration[];
  onCancel: () => void;
  onPublished: (result: PublishResponse) => void;
}

export function PublishConfirmCard({
  draft, integrations, onCancel, onPublished,
}: PublishConfirmCardProps) {
  const connectedSet = useMemo(
    () => new Set(integrations.filter(i => i.status === "active").map(i => i.destination)),
    [integrations],
  );

  const [selected, setSelected] = useState<Set<Destination>>(
    () => new Set(draft.destinations.filter(d => connectedSet.has(d))),
  );
  const [publishing, setPublishing] = useState(false);

  const hardViolationsByDest = useMemo(() => {
    const map = new Map<string, number>();
    for (const w of draft.policy_warnings || []) {
      if (w.severity === "hard") {
        map.set(w.destination, (map.get(w.destination) || 0) + 1);
      }
    }
    return map;
  }, [draft.policy_warnings]);

  // #133: a destination that goes live needs a tested, approved content-studio
  // version. The server says which are blocked and why; null while loading.
  const [gate, setGate] = useState<PublishGate | null>(null);
  const [importing, setImporting] = useState(false);
  useEffect(() => {
    publishingApi.getGate(draft.id).then(setGate)
      .catch(() => setGate({ linked: false, content_draft: null, destinations: {} }));
  }, [draft.id]);
  const gateFor = (d: Destination) => gate?.destinations?.[d];
  const gateBlocked = (d: Destination) => gate == null || (gateFor(d)?.ready === false);

  const toggle = (d: Destination) => {
    const next = new Set(selected);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelected(next);
  };

  const anyBlocked = Array.from(selected).some(d => (hardViolationsByDest.get(d) || 0) > 0);
  const anyDisconnected = Array.from(selected).some(d => !connectedSet.has(d));
  const anyGated = Array.from(selected).some(gateBlocked);
  const canPublish = selected.size > 0 && !anyBlocked && !anyDisconnected && !anyGated && !publishing;

  const bringIn = async () => {
    setImporting(true);
    try {
      const r = await publishingApi.toContentStudio(draft.id);
      toast.success(`Brought into the content studio as draft #${r.draft_id}: test and approve it there, then send it back`);
    } catch (e: any) {
      toast.error(e.message || "Could not bring it into the content studio");
    } finally {
      setImporting(false);
    }
  };

  const handlePublish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const res = await publishingApi.publish(draft.id, {
        destinations: Array.from(selected),
      });
      onPublished(res);
      if (res.overall_status === "published") {
        toast.success("Published to all destinations");
      } else if (res.overall_status === "partially_published") {
        toast.warning("Some destinations failed — check results below");
      } else {
        toast.error("Publishing failed");
      }
    } catch (e: any) {
      toast.error(e.message || "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      style={{
        borderRadius: 12,
        border: "2px solid rgba(13,148,136,0.25)",
        backgroundColor: "rgba(13,148,136,0.03)",
        padding: 14,
        display: "flex", flexDirection: "column", gap: 12,
      }}
    >
      <Alert>
        <Send />
        <AlertTitle>Confirm publish</AlertTitle>
        <AlertDescription>
          Review the destinations below. Content will go live as soon as you click Publish.
        </AlertDescription>
      </Alert>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {draft.destinations.map(d => {
          const connected = connectedSet.has(d);
          const hardCount = hardViolationsByDest.get(d) || 0;
          const checked = selected.has(d);
          const gateBlockers = gateFor(d)?.ready === false ? gateFor(d)!.blockers : [];
          const blocked = hardCount > 0 || !connected || gateBlockers.length > 0;

          return (
            <DestinationRow
              key={d}
              destination={d}
              checked={checked}
              connected={connected}
              hardCount={hardCount}
              blocked={blocked}
              gateNote={gate == null ? "Checking the publish gate…"
                : gateBlockers.map(b => b.message).join("; ") || (gateFor(d)?.live === false ? "Goes as a WordPress draft: not gated" : "")}
              onToggle={() => toggle(d)}
            />
          );
        })}
      </div>

      {anyGated && gate != null && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            A live destination publishes only a draft version that was tested in the wind tunnel and approved, sent
            here unchanged from the content studio.{" "}
            {!gate.linked && (
              <button onClick={bringIn} disabled={importing}
                style={{ textDecoration: "underline", background: "none", border: "none", padding: 0,
                         cursor: "pointer", color: "inherit", fontWeight: 600 }}>
                {importing ? "Bringing it in…" : "Bring this text into the content studio"}
              </button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {anyBlocked && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            One or more selected destinations have policy violations. Go back
            to the preview and fix them, or uncheck that destination.
          </AlertDescription>
        </Alert>
      )}

      {anyDisconnected && !anyBlocked && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            One or more selected destinations aren't connected. Connect them in
            Integrations or uncheck them.
          </AlertDescription>
        </Alert>
      )}

      <div style={{
        display: "flex", gap: 8, justifyContent: "flex-end",
        flexWrap: "wrap", paddingTop: 4,
      }}>
        <button
          onClick={onCancel}
          disabled={publishing}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "8px 14px", borderRadius: 8,
            border: "1px solid var(--border)", backgroundColor: "var(--muted)",
            cursor: publishing ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: 600, color: "var(--foreground)",
          }}
        >
          <X style={{ width: 12, height: 12 }} />
          Cancel
        </button>
        <button
          onClick={handlePublish}
          disabled={!canPublish}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 18px", borderRadius: 8,
            border: "none",
            backgroundColor: canPublish ? "#10B981" : "var(--border)",
            color: "white",
            cursor: canPublish ? "pointer" : "not-allowed",
            fontSize: 13, fontWeight: 600,
          }}
        >
          {publishing
            ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
            : <Send style={{ width: 12, height: 12 }} />
          }
          {publishing
            ? "Publishing…"
            : `Publish to ${selected.size} ${selected.size === 1 ? "destination" : "destinations"}`
          }
        </button>
      </div>
    </div>
  );
}

// ─── Destination row ─────────────────────────────────────────────────────────

function DestinationRow({
  destination, checked, connected, hardCount, blocked, gateNote, onToggle,
}: {
  destination: Destination;
  checked: boolean;
  connected: boolean;
  hardCount: number;
  blocked: boolean;
  gateNote?: string;
  onToggle: () => void;
}) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF" },
    wordpress: { Icon: Globe,     color: "#21759B" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2" },
  }[destination];

  return (
    <button
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", borderRadius: 9,
        border: `1px solid ${checked ? "rgba(13,148,136,0.4)" : "var(--border)"}`,
        backgroundColor: checked ? "rgba(13,148,136,0.06)" : "var(--background)",
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div
        style={{
          width: 16, height: 16, borderRadius: 4,
          border: `2px solid ${checked ? "#0D9488" : "var(--border)"}`,
          backgroundColor: checked ? "#0D9488" : "transparent",
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      <div style={{
        width: 28, height: 28, borderRadius: 7,
        backgroundColor: "var(--muted)", border: "1px solid var(--border)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <meta.Icon style={{ width: 14, height: 14, color: meta.color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
          {destinationLabel(destination)}
        </div>
        <div style={{
          fontSize: 11, marginTop: 2,
          color: blocked ? "#EF4444" : connected ? "#10B981" : "var(--muted-foreground)",
          fontWeight: 600,
        }}>
          {!connected && "Not connected"}
          {connected && hardCount > 0 && `${hardCount} policy issue${hardCount > 1 ? "s" : ""}`}
          {connected && hardCount === 0 && !blocked && "Ready to publish"}
          {connected && hardCount === 0 && blocked && "Not publishable yet"}
        </div>
        {gateNote && (
          <div style={{ fontSize: 11, marginTop: 2, color: "var(--muted-foreground)" }}>{gateNote}</div>
        )}
      </div>
    </button>
  );
}

// ─── Results card (rendered AFTER publish) ───────────────────────────────────

export function PublishResultsCard({
  result, onDismiss,
}: {
  result: PublishResponse;
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid var(--border)",
        backgroundColor: "var(--muted)",
        padding: 14,
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {result.overall_status === "published" && (
          <CheckCircle2 style={{ width: 18, height: 18, color: "#10B981" }} />
        )}
        {result.overall_status === "partially_published" && (
          <AlertTriangle style={{ width: 18, height: 18, color: "#D97706" }} />
        )}
        {result.overall_status === "failed" && (
          <AlertTriangle style={{ width: 18, height: 18, color: "#EF4444" }} />
        )}
        <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "var(--foreground)" }}>
          {result.overall_status === "published" && "Published successfully"}
          {result.overall_status === "partially_published" && "Partially published"}
          {result.overall_status === "failed" && "Publishing failed"}
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            padding: 5, borderRadius: 6, border: "none",
            backgroundColor: "transparent", cursor: "pointer",
            color: "var(--muted-foreground)",
          }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {result.results.map((r, i) => (
          <div
            key={i}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px", borderRadius: 8,
              backgroundColor: "var(--background)",
              border: "1px solid var(--border)",
              fontSize: 12,
            }}
          >
            {r.status === "published"
              ? <CheckCircle2 style={{ width: 13, height: 13, color: "#10B981", flexShrink: 0 }} />
              : <AlertTriangle style={{ width: 13, height: 13, color: "#EF4444", flexShrink: 0 }} />
            }
            <span style={{ fontWeight: 700, color: "var(--foreground)" }}>
              {destinationLabel(r.destination)}
            </span>
            <span style={{
              flex: 1, minWidth: 0,
              color: r.status === "published" ? "var(--muted-foreground)" : "#EF4444",
              overflowWrap: "anywhere", wordBreak: "break-word",
            }}>
              {r.status === "published"
                ? (r.external_url ? "Live" : "Sent")
                : (r.error || "Failed")
              }
            </span>
            {r.external_url && (
              <a
                href={r.external_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  color: "#0D9488", fontWeight: 600, fontSize: 12,
                  textDecoration: "none", flexShrink: 0,
                }}
              >
                View
                <ExternalLink style={{ width: 10, height: 10 }} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}